import { Avatar, Box, Spinner, Text, Textarea, TextareaProps, VStack } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getMentionable, Mentionable } from "@lubbezposrednio-frontend/core";

const MENTION_RE = /@\[([^\]]+)\]\((@PL-[A-Za-z0-9-]+)\)/g;
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]}'"])/g;

// avatar_url z backendu jest ścieżką względną (rails_blob_path) — dopinamy host backendu.
export const mediaUrl = (path?: string | null) => (path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : undefined);

export const relTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "przed chwilą";
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} godz. temu`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} dni temu`;
  return new Date(iso).toLocaleDateString("pl-PL");
};

// Renderuje treść UGC: zamienia @[Imię](@PL-…) na link do profilu i URL-e na klikalne linki.
// Wejście to czysty tekst (backend strip_tags) — bezpieczne, bez dangerouslySetInnerHTML.
export const RichText: React.FC<{ children: string; fontSize?: string; color?: string }> = ({ children, fontSize = "15px", color = "gray.700" }) => {
  const nodes: React.ReactNode[] = [];
  let key = 0;

  // Najpierw dzielimy po wzmiankach, w kawałkach „tekstowych" linkujemy URL-e.
  let lastIndex = 0;
  const text = children || "";
  const matches = Array.from(text.matchAll(MENTION_RE));

  const pushLinkified = (chunk: string) => {
    let li = 0;
    for (const um of Array.from(chunk.matchAll(URL_RE))) {
      const url = um[0];
      const start = um.index ?? 0;
      if (start > li) nodes.push(<React.Fragment key={key++}>{chunk.slice(li, start)}</React.Fragment>);
      nodes.push(
        <Box as="a" key={key++} href={url} target="_blank" rel="noopener noreferrer nofollow" color="#7b2ff7" fontWeight="600" textDecoration="underline" wordBreak="break-all" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          {url}
        </Box>
      );
      li = start + url.length;
    }
    if (li < chunk.length) nodes.push(<React.Fragment key={key++}>{chunk.slice(li)}</React.Fragment>);
  };

  for (const m of matches) {
    const start = m.index ?? 0;
    if (start > lastIndex) pushLinkified(text.slice(lastIndex, start));
    const name = m[1];
    const ref = m[2];
    nodes.push(
      <Box as={Link} key={key++} to={`/profil/${ref}`} color="primary" fontWeight="700" _hover={{ textDecoration: "underline" }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        @{name}
      </Box>
    );
    lastIndex = start + m[0].length;
  }
  if (lastIndex < text.length) pushLinkified(text.slice(lastIndex));

  return (
    <Text color={color} fontSize={fontSize} whiteSpace="pre-wrap" wordBreak="break-word">
      {nodes}
    </Text>
  );
};

// Textarea z autouzupełnianiem @wzmianek (osoby z kontem w grupie). Wstawia token @[Imię](@PL-…).
type MentionTextareaProps = Omit<TextareaProps, "onChange" | "value"> & {
  value: string;
  onChange: (v: string) => void;
  groupId: string;
};

export const MentionTextarea: React.FC<MentionTextareaProps> = ({ value, onChange, groupId, ...rest }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<Mentionable[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  // Wykryj „@słowo" tuż przed kursorem (świeżo wpisywana wzmianka, nie gotowy token).
  const detect = (text: string, caret: number) => {
    const upto = text.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([\p{L}0-9_]{0,30})$/u);
    setQuery(m ? m[1] : null);
    setActive(0);
  };

  useEffect(() => {
    // Brak grupy (np. post portalowy) → bez autouzupełniania @wzmianek (uniknięcie zapytania do //wall → 404).
    if (query === null || !groupId) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      getMentionable(groupId, query)
        .then(r => setResults(r.data.results ?? []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query, groupId]);

  const insert = (person: Mentionable) => {
    const el = ref.current;
    const caret = el?.selectionStart ?? value.length;
    const upto = value.slice(0, caret);
    const after = value.slice(caret);
    const replaced = upto.replace(/@([\p{L}0-9_]{0,30})$/u, `@[${person.name}](${person.profile_ref}) `);
    const next = replaced + after;
    onChange(next);
    setQuery(null);
    setResults([]);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = replaced.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (query !== null && results.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => (a + 1) % results.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => (a - 1 + results.length) % results.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insert(results[active]); return; }
      if (e.key === "Escape") { setQuery(null); return; }
    }
    rest.onKeyDown?.(e);
  };

  return (
    <Box position="relative" w="100%">
      <Textarea
        ref={ref}
        value={value}
        onChange={e => {
          onChange(e.target.value);
          detect(e.target.value, e.target.selectionStart ?? e.target.value.length);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        {...rest}
      />
      {query !== null && (loading || results.length > 0) && (
        <Box position="absolute" zIndex={20} mt="4px" bg="white" borderRadius="12px" border="1px solid rgba(67,0,152,0.12)" boxShadow="0 12px 30px rgba(67,0,152,0.16)" w="100%" maxW="340px" overflow="hidden">
          {loading && results.length === 0 ? (
            <Box p="12px" textAlign="center"><Spinner size="sm" color="primary" /></Box>
          ) : (
            <VStack align="stretch" spacing={0} maxH="240px" overflowY="auto">
              {results.map((r, i) => (
                <Box key={r.profile_ref} px="12px" py="9px" cursor="pointer" bg={i === active ? "rgba(67,0,152,0.07)" : "transparent"} _hover={{ bg: "rgba(67,0,152,0.07)" }} onMouseDown={e => { e.preventDefault(); insert(r); }} display="flex" alignItems="center" gap="10px">
                  <Avatar size="xs" name={r.name} src={mediaUrl(r.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
                  <Box minW={0}>
                    <Text fontSize="13.5px" fontWeight="700" color="primary" noOfLines={1}>{r.name}</Text>
                    <Text fontSize="11px" color="gray.400" noOfLines={1}>{r.profile_ref}</Text>
                  </Box>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};
